import React, { useState } from 'react';
import {
  IonContent,
  IonButton,
} from '@ionic/react';
import {
  Box,
  FormControlLabel,
  RadioGroup,
  Stack,
  Typography,
  Radio,
  TextField,
  MenuItem,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import PageWithBackButton from 'components/generic/PageWithBackButton';
import { TaupeTextField } from 'components/StyledInput';
import './GoalsCreate.css';
import { useAuthContext } from 'providers/auth-provider';
import appStrings from 'common/app_strings';
import appColors from 'common/app_colors';

const { goals: { goalsCreate: STRINGS, goalsCommon: COMMON_STRINGS }, common } = appStrings;
const { text, container, selectInput } = appColors;

const GoalsCreate = ({
  onBack, goalData, onFinalSubmit,
}) => {
  const { user } = useAuthContext();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({ mode: 'onChange' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data) => {
    if (isSubmitting) return;

    const goalFormData = {
      content: data.content,
      visibility: data.visibility,
      status: data.status,
      user_id: user.id,
      type: goalData.type,
      subtypes: goalData.subtypes,
    };
    setIsSubmitting(true);

    await onFinalSubmit(goalFormData);
  };

  const bodyInputRef = React.useRef(null);

  return (
    <PageWithBackButton onBack={onBack} backButtonText="">
      <IonContent fullscreen>
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          <Box mx={3}>
            <div className="goals-smart-wrapper">
              <Typography
                variant="h4"
                fontWeight={700}
                fontSize={20}
                sx={{ textAlign: 'center', mr: 1, mb: 5 }}
              >
                {STRINGS.title}
              </Typography>
            </div>
            <Stack spacing={3} mb={6}>
              <Box
                sx={{
                  bgcolor: container.white,
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Controller
                  control={control}
                  name="content"
                  rules={{ required: true }}
                  defaultValue={goalData?.selected_template}
                  render={({ field }) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...field}
                      ref={bodyInputRef}
                      variant="standard"
                      multiline
                      minRows={4}
                      maxRows={5}
                      onFocus={() => bodyInputRef.current.scrollIntoView({ behavior: 'smooth' })}
                      fullWidth
                      placeholder="I want to improve my mental health by meditating 3 times per week."
                      inputProps={{
                        maxLength: 200,
                        autoCapitalize: 'on',
                        inputMode: 'text',
                      }}
                      // eslint-disable-next-line react/jsx-no-duplicate-props
                      InputProps={{ disableUnderline: true }}
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  bgcolor: container.white,
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    color: text.lightBlack,
                    fontSize: 16,
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  {COMMON_STRINGS.shareWith}
                </Typography>
                <Controller
                  control={control}
                  name="visibility"
                  defaultValue="public"
                  rules={{ required: true }}
                  render={({ field }) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <RadioGroup {...field}>

                      <FormControlLabel
                        value="public"
                        control={(
                          <Radio
                            sx={{
                              '&, &.Mui-checked': {
                                color: selectInput.radioSelect,
                              },
                            }}
                          />
                        )}
                        label={(
                          <Typography sx={{ color: text.lightBlack }}>
                            {COMMON_STRINGS.makePublic}
                          </Typography>
                        )}
                      />
                      <FormControlLabel
                        value="team"
                        control={(
                          <Radio
                            sx={{
                              '&, &.Mui-checked': {
                                color: selectInput.radioSelect,
                              },
                            }}
                          />
                        )}
                        label={(
                          <Typography sx={{ color: text.lightBlack }}>
                            {COMMON_STRINGS.myPanel}
                          </Typography>
                        )}
                      />
                    </RadioGroup>
                  )}
                />
              </Box>
              <Box
                sx={{
                  bgcolor: container.white,
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    color: text.lightBlack,
                    fontSize: 16,
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  {COMMON_STRINGS.goalProgress}
                </Typography>
                <Controller
                  control={control}
                  name="status"
                  defaultValue="in_progress"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TaupeTextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...field}
                      select
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </TaupeTextField>
                  )}
                />
              </Box>
            </Stack>
          </Box>
          <IonButton
            size="large"
            expand="block"
            shape="round"
            className="goals-create-submit-btn"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || !isValid}
          >
            {common.submit}
          </IonButton>
        </form>
      </IonContent>
    </PageWithBackButton>
  );
};

GoalsCreate.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  goalData: PropTypes.shape({
    type: PropTypes.string.isRequired,
    subtypes: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      subtype_id: PropTypes.number,
      id: PropTypes.number,
    })),
    template: PropTypes.string,
  }).isRequired,
};

export default GoalsCreate;
